import { encodeAbiParameters,encodeFunctionData,getAbiItem,keccak256,toHex,type Address } from 'viem';
import { z } from 'zod';
import { hashPreparedCalls } from './calls.js';
import { experimentalMultiMarketAbi as abi,experimentalMultiMarketVersion } from './conditionalMultiMarketAbi.js';

const decimal=/^(0|[1-9][0-9]{0,77})$/;
const uint=(bits:number,positive=false)=>z.string().regex(decimal).refine(value=>decimal.test(value)
 && BigInt(value)>=(positive?1n:0n) && BigInt(value)<(1n<<BigInt(bits)));
const address=z.string().regex(/^0x[0-9a-fA-F]{40}$/).refine(value=>!/^0x0{40}$/i.test(value))
 .transform(value=>value.toLowerCase() as Address);
const hash=z.string().regex(/^0x[0-9a-fA-F]{64}$/).transform(value=>value.toLowerCase() as `0x${string}`);
const trigger=z.strictObject({threshold:uint(256),atOrBelow:z.boolean(),latchAfterFirstFill:z.boolean()});
const leg=z.strictObject({minimumTotalOutput:uint(128,true),maximumFeeBps:z.number().int().min(0).max(10000),expiry:uint(256),trigger});
const profile=z.strictObject({curve:address,base:address,quote:address,curveCodeHash:hash,policyHash:hash,
 canonicalRouter:address,routerCodeHash:hash,canonicalManager:address,managerCodeHash:hash,
 adapterCodeHash:hash,canonicalTickSpacing:z.number().int().min(1).max(32767)});
const terms=z.discriminatedUnion('kind',[
 z.strictObject({kind:z.literal('independent'),buy:z.boolean(),maximumInput:uint(128,true),leg}),
 z.strictObject({kind:z.literal('linked'),maximumInput:uint(128,true),first:leg,second:leg}),
]);
const schema=z.strictObject({schemaVersion:z.literal(1),registrationId:z.uuid(),userId:z.string().min(1).max(128),
 version:z.literal(experimentalMultiMarketVersion),chainId:z.literal(4663),engine:address,vault:address,owner:address,
 engineCodeHash:hash,marketId:hash,globalEpoch:uint(256,true),marketRevision:uint(256,true),profile,
 source:z.strictObject({blockNumber:uint(256),blockHash:hash,observedAt:z.iso.datetime()}),terms});
export type ConditionalMultiMarketIntent=z.input<typeof schema>;

/** Immutable pre-ID review record for the context-bound experiment only.
 * Valid shape and hashes prove neither profile provenance nor current ownership,
 * canonicality, freshness or permission to submit. Historical records remain
 * readable; stale context must fail on chain rather than be silently refreshed.
 * Existing single-market storage/readers are deliberately not widened here.
 */
export function readConditionalMultiMarketIntent(input:unknown){
 const record=schema.parse(input);
 if(record.engine===record.vault || record.profile.base===record.profile.quote)throw new Error('Invalid market identity');
 const computedMarketId=keccak256(encodeAbiParameters([{type:'uint256'},{type:'address'},{type:'address'},{type:'address'}],
  [BigInt(record.chainId),record.engine,record.profile.base,record.profile.quote]));
 if(record.marketId!==computedMarketId)throw new Error('Market identity mismatch');
 const decodeLeg=(value:z.infer<typeof leg>)=>({minimumTotalOutput:BigInt(value.minimumTotalOutput),
  maximumFeeBps:value.maximumFeeBps,expiry:BigInt(value.expiry),
  trigger:{...value.trigger,threshold:BigInt(value.trigger.threshold)}});
 const expected={globalEpoch:BigInt(record.globalEpoch),marketRevision:BigInt(record.marketRevision)};
 let data:`0x${string}`;
 if(record.terms.kind==='linked'){
  const first=decodeLeg(record.terms.first),second=decodeLeg(record.terms.second);
  if(first.trigger.threshold===0n || second.trigger.threshold===0n)throw new Error('Linked exits require explicit triggers');
  data=encodeFunctionData({abi,functionName:'registerAllocation',args:[record.marketId,expected,
   BigInt(record.terms.maximumInput),first,second]});
 }else{
  const decoded=decodeLeg(record.terms.leg);
  if(decoded.trigger.threshold===0n && (decoded.trigger.atOrBelow || decoded.trigger.latchAfterFirstFill))
   throw new Error('Untriggered limit order cannot carry trigger flags');
  data=encodeFunctionData({abi,functionName:'register',args:[record.marketId,expected,record.terms.buy,
   BigInt(record.terms.maximumInput),decoded.minimumTotalOutput,decoded.maximumFeeBps,decoded.expiry,decoded.trigger]});
 }
 const profileHash=keccak256(encodeAbiParameters(getAbiItem({abi,name:'configureMarket'}).inputs,[record.profile]));
 const recordJson=JSON.stringify(record),call={to:record.engine,value:0n,data};
 return {record,recordJson,intentHash:keccak256(toHex(recordJson)),profileHash,call,callsHash:hashPreparedCalls([call]),
  executionAuthorized:false as const,registrationProven:false as const,submissionAuthorized:false as const,
  verification:'independent-owner-runtime-market-profile-source-and-canonical-registration-required' as const};
}
